import axios from 'axios';
import { API_TYPE, API_ERROR_CODES, API_DEFAULT_TIMEOUT } from '../../constants';

async function getAxiosInstance( endpoint, timeout ) {
    if (this.requiresAuthentication && !this.token) {
        throw new Error('no token found');
    }
    const headers =  this.token !== 'noauth' ? { Authorization: `Bearer ${this.token}`, } : undefined;

    const instance = axios.create({
        baseURL: endpoint,
        timeout,
        headers
    });
    instance.interceptors.response.use(
        response => response,
        async error => {
            const originalRequest = error.config;
            const { status } = error.response;
            if (status === API_ERROR_CODES.unauthorized && !originalRequest._retry) {
                originalRequest._retry = true;
                this.token = await this.refreshFunction();
                originalRequest.headers['Authorization'] = `Bearer ${this.token}`;
                return instance(originalRequest);
            }
            return Promise.reject(error);
        }
    );
    return instance;
}

/**
 * Api Get call
 * @param  {string} url api url
 * @param  {object} params querystring
 * @param  {string} endpoint api or webapi endpoint
 * @param  {number} timeout custom request timeout, defaults to 10000 ms
 * @returns {Promise} Promise object returns the request
 */
async function axiosGet( url, params, endpoint = this.endpoint, timeout = API_DEFAULT_TIMEOUT) {
    const axiosInstance = await this.getAxiosInstance(endpoint, timeout);
    return axiosInstance.get(url, { params });
}

/**
 * Api Post call
 * @param  {string} url api url
 * @param  {object} data data to post
 * @param  {string} endpoint api or webapi endpoint
 * @param  {number} timeout custom request timeout, defaults to 10000 ms
 * @return {*}     [description]
 */
async function axiosPost(url, data, endpoint = this.endpoint, timeout = API_DEFAULT_TIMEOUT, config = null) {
    const axiosInstance = await this.getAxiosInstance(endpoint, timeout);
    return axiosInstance.post(url, data, config);
}

/**
 * Api Put call
 * @param  {string} url api url
 * @param  {object} data data to put
 * @param  {string} endpoint api or webapi endpoint
 * @param  {number} timeout custom request timeout, defaults to 10000 ms
 * @return {*}     [description]
 */
async function axiosPut(url, data, endpoint = this.endpoint, timeout = API_DEFAULT_TIMEOUT, config = null) {
    const axiosInstance = await this.getAxiosInstance(endpoint, timeout);
    return axiosInstance.put(url, data, config);
}

/**
 * Api Delete call
 * @param  {string} url api url
 * @param  {object} data data to post
 * @param  {string} endpoint api or webapi endpoint
 * @param  {number} timeout custom request timeout, defaults to 10000 ms
 * @return {*}     [description]
 */
async function axiosDelete(url, data, endpoint = this.endpoint, timeout = API_DEFAULT_TIMEOUT) {
    const axiosInstance = await this.getAxiosInstance(endpoint, timeout);
    return axiosInstance.delete(url, data);
}

const getEndpoint = (appConfig, apiType, defaultEndpoint) => {
    // first handle expliceit apiType parameters
    if (apiType === API_TYPE.API) {
        return appConfig[API_TYPE.API].endpoint;
    } else if (apiType?.toLowerCase() === API_TYPE.WEBAPI.toLowerCase()) {
        return appConfig[API_TYPE.WEBAPI].endpoint || appConfig.webapi.endpoint;
    }
    // otherwise default endpoint
    return defaultEndpoint;
};

const createNedGlobeApiService = (appConfig, defaultEndpoint, requiresAuthentication, token, refreshFunction) => ({
    get: axiosGet,
    post: axiosPost,
    put: axiosPut,
    delete: axiosDelete,
    getData: axiosGet,
    postData: axiosPost,
    putData: axiosPut,
    deleteData: axiosDelete,
    getAxiosInstance,
    refreshFunction,
    token,
    appConfig,
    get requiresAuthentication() {
        return requiresAuthentication;
    },
    get endpoint() {
        return getEndpoint(appConfig, null, defaultEndpoint);
    }
});

export default createNedGlobeApiService;
